import React, {useEffect, useState} from 'react';
import styled from "styled-components";
import {CustomCard} from "../../../../../components/UI/AntCustom/CustomCard";
import {CustomButton} from "../../../../../components/UI/AntCustom/CustomButton";
import {useIntl} from "react-intl";
import {defineMessages} from "react-intl";
import {ITariff} from "../../../../../store/reducers/tariffReducer/tariffReducerTypes";
import {Loader} from "../../../../../components/UI/Loader/Loader";
import {useAppDispatch, useAppSelector} from "../../../../../store/store";
import {cancelSubscription, clearCancelSubscription, resumeSubscription} from "../../../../../store/reducers/tariffReducer/tariffReducer";
import {ManageSubscriptionModal} from "./ManageSubscriptionModal";
import {TrialDaysCounter} from "./TrialDaysCounter";
import dayjs from "dayjs";

const SubscriptionHeaderMessages = defineMessages({
  addPaymentMethod: {
    id: "subscriptionHeader.addPaymentMethod",
    defaultMessage: "Add Payment Method",
  },
  changePaymentMethod: {
    id: "subscriptionHeader.changePaymentMethod",
    defaultMessage: "Change Payment Method",
  },
  manageSubscription: {
    id: "subscriptionHeader.manageSubscription",
    defaultMessage: "Manage Subscription",
  },
  paymentMethodAdded: {
    id: "subscriptionHeader.paymentMethodAdded",
    defaultMessage: "Payment method added",
  },
  currentPlan: {
    id: "subscriptionHeader.currentPlan",
    defaultMessage: "Current plan",
  },
  validUntil: {
    id: "subscriptionHeader.validUntil",
    defaultMessage: "Valid until",
  },
  validUntilDescription: {
    id: "subscriptionHeader.validUntilDescription",
    defaultMessage: "Date when your subscription access ends",
  },
  autoRenewal: {
    id: "subscriptionHeader.autoRenewal",
    defaultMessage: "Auto renewal",
  },
  autoRenewalDescription: {
    id: "subscriptionHeader.autoRenewalDescription",
    defaultMessage: "Whether the subscription will automatically renew at the end of the billing period",
  },
  yes: {
    id: "subscriptionHeader.yes",
    defaultMessage: "Yes",
  },
  no: {
    id: "subscriptionHeader.no",
    defaultMessage: "No",
  },
  cancel: {
    id: "subscriptionHeader.cancel",
    defaultMessage: "Cancel",
  },
  subscription: {
    id: "subscriptionHeader.subscription",
    defaultMessage: "Subscription",
  },
  pro: {
    id: "subscriptionHeader.pro",
    defaultMessage: "Pro",
  },
  premium: {
    id: "subscriptionHeader.premium",
    defaultMessage: "Premium",
  },
  noActivePlan: {
    id: "subscriptionHeader.noActivePlan",
    defaultMessage: "No active plan",
  },
  subscriptionStatus: {
    id: "subscriptionHeader.subscriptionStatus",
    defaultMessage: "Subscription status",
  },
  trialEndsAt: {
    id: "subscriptionHeader.trialEndsAt",
    defaultMessage: "Trial ends at",
  },
  trialEndedAt: {
    id: "subscriptionHeader.trialEndedAt",
    defaultMessage: "Trial ended",
  },
  gracePeriodUntil: {
    id: "subscriptionHeader.gracePeriodUntil",
    defaultMessage: "Grace period until",
  },
  statusActive: {
    id: "subscriptionHeader.statusActive",
    defaultMessage: "Active",
  },
  statusTrialing: {
    id: "subscriptionHeader.statusTrialing",
    defaultMessage: "Trialing (billing starts after trial)",
  },
  statusPastDue: {
    id: "subscriptionHeader.statusPastDue",
    defaultMessage: "Past Due",
  },
  statusCanceled: {
    id: "subscriptionHeader.statusCanceled",
    defaultMessage: "Canceled",
  },
  statusUnpaid: {
    id: "subscriptionHeader.statusUnpaid",
    defaultMessage: "Unpaid",
  },
  statusIncomplete: {
    id: "subscriptionHeader.statusIncomplete",
    defaultMessage: "Incomplete",
  },
  statusNotStarted: {
    id: "subscriptionHeader.statusNotStarted",
    defaultMessage: "Not started",
  },
});

const HeaderContent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  
  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
    
    button {
      width: 100%;
    }
  }
`;

const PlanInfo = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
`;

const PlanName = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #000000;
`;

const PlanDetails = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  font-size: 14px;
  color: #666666;
`;

const PlanDetailRow = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 8px;
`;

const StatusBadge = styled.span<{status?: string}>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${props => {
    switch (props.status) {
      case 'active':
      case 'trialing':
        return '#52c41a';
      case 'past_due':
        return '#faad14';
      case 'canceled':
        return '#d9d9d9';
      case 'unpaid':
      case 'incomplete':
        return '#ff4d4f';
      case 'not_started':
        return '#d9d9d9';
      default:
        return '#d9d9d9';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'active':
      case 'trialing':
      case 'past_due':
        return '#ffffff';
      default:
        return '#000000';
    }
  }};
`;

interface ISubscriptionHeader {
  tariff?: ITariff;
  subscriptionPrices?: {
    pricePerSeatCents?: number;
    pricePerAircraftCents?: number;
    currency?: string;
  };
  activeUsers?: number;
  activeAircraft?: number;
  isOperator?: boolean;
  isLoading?: boolean;
}

const getPlanTypeName = (intl: any, type?: number): string => {
  switch (type) {
    case 1:
      return intl.formatMessage(SubscriptionHeaderMessages.subscription);
    case 2:
      return intl.formatMessage(SubscriptionHeaderMessages.pro);
    case 3:
      return intl.formatMessage(SubscriptionHeaderMessages.premium);
    default:
      return '';
  }
};

const getStatusLabel = (intl: any, status?: string): string => {
  switch (status) {
    case 'active':
      return intl.formatMessage(SubscriptionHeaderMessages.statusActive);
    case 'trialing':
      return intl.formatMessage(SubscriptionHeaderMessages.statusTrialing);
    case 'past_due':
      return intl.formatMessage(SubscriptionHeaderMessages.statusPastDue);
    case 'canceled':
      return intl.formatMessage(SubscriptionHeaderMessages.statusCanceled);
    case 'unpaid':
      return intl.formatMessage(SubscriptionHeaderMessages.statusUnpaid);
    case 'incomplete':
      return intl.formatMessage(SubscriptionHeaderMessages.statusIncomplete);
    case 'not_started':
      return intl.formatMessage(SubscriptionHeaderMessages.statusNotStarted);
    default:
      return status || '';
  }
};

export const SubscriptionHeader: React.FC<ISubscriptionHeader> = ({
  tariff,
  subscriptionPrices,
  activeUsers,
  activeAircraft,
  isOperator = false,
  isLoading = false,
}) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [isManageSubscriptionModalVisible, setIsManageSubscriptionModalVisible] = useState(false);
  
  const cancelSubscriptionState = useAppSelector(state => state.tariff.cancelSubscription);

  useEffect(() => {
    if (cancelSubscriptionState.isSuccess) {
      dispatch(clearCancelSubscription());
    }
  }, [cancelSubscriptionState.isSuccess, dispatch]);
  
  // Защита от undefined - после всех хуков
  // Не возвращаем null, чтобы кнопка всегда была видна
  const activePackage = tariff?.companyTariffPackages?.find(pkg => pkg.active);
  const isTrial = activePackage?.tariffPackageType === 1;
  const subscriptionStatus = activePackage?.subscriptionStatus;
  const isSubscriptionActive = subscriptionStatus === 'active';
  const isCanceled = subscriptionStatus === 'canceled';
  const isPastDue = subscriptionStatus === 'past_due';

  const trialEndAt = activePackage?.trialEndAt;
  const isTrialActive = trialEndAt ? dayjs(trialEndAt).isAfter(dayjs()) : false;
  const isTrialEnded = trialEndAt ? dayjs(trialEndAt).isBefore(dayjs(), 'day') : false;
  const neverHadSubscription = !activePackage?.stripeSubscriptionId || subscriptionStatus === 'not_started';

  // Derive display status: not_started when activePackage exists but no Stripe subscription and no active trial
  const displayStatus = (() => {
    if (!activePackage) return undefined;
    if (subscriptionStatus) return subscriptionStatus;
    if (activePackage.stripeSubscriptionId) return subscriptionStatus;
    if (isTrialActive) return 'trialing';
    return 'not_started';
  })();

  const handleCancel = () => {
    if (activePackage?.companyTariffPackageId) {
      dispatch(cancelSubscription({companyTariffPackageId: activePackage.companyTariffPackageId}));
    }
  };

  const handleManageSubscriptionSuccess = () => {
    // Tariff is refreshed by attachPaymentMethod saga or resumeSubscription saga
  };

  const planTitle = (
    <PlanInfo>
      <PlanName>
        {activePackage 
          ? getPlanTypeName(intl, activePackage.tariffPackageType)
          : intl.formatMessage(SubscriptionHeaderMessages.noActivePlan)
        }
      </PlanName>
      {activePackage && (
        <PlanDetails>
          {displayStatus && (
            <PlanDetailRow>
              <div>{intl.formatMessage(SubscriptionHeaderMessages.subscriptionStatus)}:</div>
              <StatusBadge status={displayStatus}>
                {getStatusLabel(intl, displayStatus)}
              </StatusBadge>
            </PlanDetailRow>
          )}
          {activePackage.trialEndAt && neverHadSubscription && isTrialEnded && (
            <PlanDetailRow>
              <div>{intl.formatMessage(SubscriptionHeaderMessages.trialEndedAt)}:</div>
              <div>{dayjs(activePackage.trialEndAt).format('DD.MM.YYYY')}</div>
            </PlanDetailRow>
          )}
          {activePackage.gracePeriodUntil && (
            <PlanDetailRow>
              <div>{intl.formatMessage(SubscriptionHeaderMessages.gracePeriodUntil)}:</div>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                {dayjs(activePackage.gracePeriodUntil).format('DD.MM.YYYY')}
                <TrialDaysCounter
                  trialEndAt={activePackage.trialEndAt}
                  gracePeriodUntil={activePackage.gracePeriodUntil}
                />
              </div>
            </PlanDetailRow>
          )}
          {activePackage.tariffEnd && !isTrialActive && !dayjs(activePackage.tariffEnd).isBefore(dayjs(), 'day') && (
            <PlanDetailRow>
              <div>{intl.formatMessage(SubscriptionHeaderMessages.validUntil)}:</div>
              <div>{dayjs(activePackage.tariffEnd).subtract(1, 'day').format('DD.MM.YYYY')}</div>
            </PlanDetailRow>
          )}
          <PlanDetailRow>
            <div>{intl.formatMessage(SubscriptionHeaderMessages.autoRenewal)}:</div>
            <div>
              {activePackage.autoRenewal 
                ? intl.formatMessage(SubscriptionHeaderMessages.yes)
                : intl.formatMessage(SubscriptionHeaderMessages.no)
              }
            </div>
          </PlanDetailRow>
        </PlanDetails>
      )}
    </PlanInfo>
  );

  const handleManageSubscriptionClick = () => {
    setIsManageSubscriptionModalVisible(true);
  };

  const planExtra = (
    <ButtonsContainer>
      <CustomButton 
        type={isSubscriptionActive ? "default" : "primary"}
        htmlType="button"
        onClick={handleManageSubscriptionClick}
      >
        {intl.formatMessage(SubscriptionHeaderMessages.manageSubscription)}
      </CustomButton>
      {activePackage && !isTrial && !isCanceled && (
        <CustomButton 
          danger 
          onClick={handleCancel}
          loading={cancelSubscriptionState.isLoading}
          disabled={cancelSubscriptionState.isLoading}
        >
          {intl.formatMessage(SubscriptionHeaderMessages.cancel)}
        </CustomButton>
      )}
    </ButtonsContainer>
  );

  return (
    <>
    <CustomCard width={"max"} style={{ width: '100%' }}>
      {isLoading ? (
        <Loader />
      ) : (
        <HeaderContent>
          {planTitle}
          {planExtra}
        </HeaderContent>
      )}
    </CustomCard>
    
    <ManageSubscriptionModal
      open={isManageSubscriptionModalVisible}
      onClose={() => setIsManageSubscriptionModalVisible(false)}
      activePackage={activePackage ?? null}
      subscriptionPrices={subscriptionPrices}
      activeUsers={activeUsers}
      activeAircraft={activeAircraft}
      isOperator={isOperator}
      onSuccess={handleManageSubscriptionSuccess}
    />
    </>
  );
};

